import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { COLORS } from "@/utils/constants/colors";
import { useDataQueries } from '@/utils/hooks/useDataQueries';
import { cn } from "@/lib/utils";

interface Props {
    handleSelectChange: (selectedStatusOption: any) => void;
    options?: any;
    selectedStatusOption: any;
    setSelectedStatusOption: (selectedStatusOption: any) => void;
    colorStyles: any;
    handleReset: (() => void | undefined) | undefined;
    handleApplyFilter: (() => void | undefined) | undefined;
    filter: any;
    setFilter: (filter: any) => void;
    groups?: any;
    roles?: any;
    title?: string;
    setPageNo?: (pageNo: number) => void;
    id: string;
}

const ALL_FILTER_VALUE = "__all__";

type FilterSelectOption = {
    value: string;
    label: string;
};

const toSelectItemValue = (value: unknown): string | null => {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    return String(value);
};

type FilterSelectFieldProps = {
    id: string;
    placeholder: string;
    value: string;
    options: FilterSelectOption[];
    onValueChange: (value: string) => void;
    className?: string;
};

const FilterSelectField = ({
    id,
    placeholder,
    value,
    options,
    onValueChange,
    className,
}: FilterSelectFieldProps) => {
    const selectedLabel = options.find((option) => option.value === value)?.label;
    const showPlaceholder = !selectedLabel || value === ALL_FILTER_VALUE;

    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    id={id}
                    className={cn("gs-filter-select-trigger mt-2", className)}
                >
                    <span className={showPlaceholder ? "gs-filter-select-trigger__placeholder" : undefined}>
                        {showPlaceholder ? placeholder : selectedLabel}
                    </span>
                    <em className="icon ni ni-chevron-down gs-filter-select-trigger__chevron" aria-hidden />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                sideOffset={1}
                className="gs-filter-select-content"
            >
                {options.map((option) => (
                    <DropdownMenuItem
                        key={option.value}
                        className="gs-filter-select-item"
                        onClick={() => onValueChange(option.value)}
                    >
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const FilterOverlay = (props: Props) => {
    const [open, setOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string>(ALL_FILTER_VALUE);
    const [selectedGroup, setSelectedGroup] = useState<string>(ALL_FILTER_VALUE);
    const [selectedRole, setSelectedRole] = useState<string>(ALL_FILTER_VALUE);
    const [ownerSearch, setOwnerSearch] = useState(props.filter?.owner || "");
    const [openCreatedAtDate, setCreatedAtDateOpen] = useState(false);
    const [openLastLoginAtDate, setLastLoginAtDateOpen] = useState(false);
    const [createdAtSelectionRange, setCreatedAtSelectionRange] = useState({ startDate: undefined, endDate: undefined, key: "selection" });
    const [lastLoginAtSelectionRange, setLastLoginAtSelectionRange] = useState({ startDate: undefined, endDate: undefined, key: "selection" });
    const [, , , , , , , , , , resetDataQueries] = useDataQueries();

    const handleStatusChange = (value: string) => {
        const option =
            value === ALL_FILTER_VALUE
                ? null
                : props.options?.find((o: any) => String(o.value) === value) ?? null;
        setSelectedStatus(value);
        props.setFilter({ ...props.filter, status: option });
    };
    const handleGroupChange = (value: string) => {
        const option =
            value === ALL_FILTER_VALUE
                ? null
                : props.groups?.find((g: any) => String(g.id) === value) ?? null;
        setSelectedGroup(value);
        props.setFilter({
            ...props.filter,
            group: option ? { label: option.name, value: option.id } : null,
        });
    };
    const handleRoleChange = (value: string) => {
        const option =
            value === ALL_FILTER_VALUE
                ? null
                : props.roles?.find((r: any) => String(r.id) === value) ?? null;
        setSelectedRole(value);
        props.setFilter({
            ...props.filter,
            role: option ? { label: option.name, value: option.id } : null,
        });
    };

    const handleCreatedAtSelectRange = (ranges: any) => {
        setCreatedAtSelectionRange(ranges.selection);
        props.setFilter({ ...props.filter, createdAtStart: ranges.selection.startDate ? format(new Date(ranges.selection.startDate), "yyyy-MM-dd") : "", createdAtEnd: ranges.selection.endDate ? format(new Date(ranges.selection.endDate), "yyyy-MM-dd") : "" });
    };
    const handleLastLoginAtSelectRange = (ranges: any) => {
        setLastLoginAtSelectionRange(ranges.selection);
        props.setFilter({ ...props.filter, lastLoginAtStart: ranges.selection.startDate ? format(new Date(ranges.selection.startDate), "yyyy-MM-dd") : "", lastLoginAtEnd: ranges.selection.endDate ? format(new Date(ranges.selection.endDate), "yyyy-MM-dd") : "" });
    };
    const handleClearCreatedAtDate = () => { setCreatedAtSelectionRange({ startDate: undefined, endDate: undefined, key: "selection" }); props.setFilter({ ...props.filter, createdAtStart: "", createdAtEnd: "" }); };
    const handleClearLastLoginAtDate = () => { setLastLoginAtSelectionRange({ startDate: undefined, endDate: undefined, key: "selection" }); props.setFilter({ ...props.filter, lastLoginAtStart: "", lastLoginAtEnd: "" }); };

    const handleApplyFilter = () => {
        setOpen(false);
        props.setFilter({ ...props.filter, owner: ownerSearch });
        props.handleApplyFilter?.();
    };

    const handleReset = () => {
        setCreatedAtSelectionRange({ startDate: undefined, endDate: undefined, key: "selection" });
        setLastLoginAtSelectionRange({ startDate: undefined, endDate: undefined, key: "selection" });
        setSelectedGroup(ALL_FILTER_VALUE);
        setSelectedRole(ALL_FILTER_VALUE);
        setSelectedStatus(ALL_FILTER_VALUE); setOwnerSearch("");
        props.setFilter({});
        resetDataQueries();
        props.handleReset?.();
    };

    const dateRangeLabel = (range: typeof createdAtSelectionRange) =>
        range.startDate && range.endDate ? `${new Date(range.startDate as string).toDateString()} - ${new Date(range.endDate as string).toDateString()}` : "";

    const statusOptions = useMemo<FilterSelectOption[]>(() => {
        const items: FilterSelectOption[] = [{ value: ALL_FILTER_VALUE, label: "All" }];
        props.options?.forEach((opt: any) => {
            const selectValue = toSelectItemValue(opt.value);
            if (selectValue !== null) {
                items.push({ value: selectValue, label: opt.label });
            }
        });
        return items;
    }, [props.options]);

    const roleOptions = useMemo<FilterSelectOption[]>(() => {
        const items: FilterSelectOption[] = [{ value: ALL_FILTER_VALUE, label: "All Roles" }];
        props.roles?.forEach((role: any) => {
            items.push({ value: String(role.id), label: role.name });
        });
        return items;
    }, [props.roles]);

    const groupOptions = useMemo<FilterSelectOption[]>(() => {
        const items: FilterSelectOption[] = [{ value: ALL_FILTER_VALUE, label: "All Groups" }];
        props.groups?.forEach((group: any) => {
            items.push({ value: String(group.id), label: group.name });
        });
        return items;
    }, [props.groups]);

    return (
        <Popover open={open} onOpenChange={setOpen} modal={false}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="light"
                    size="sm"
                    id={`${props.id}-filter-btn`}
                    className="gs-list-page-filter-btn"
                    aria-label={BUTTONS.FILTER}
                    title={BUTTONS.FILTER}
                >
                    <em className="icon ni ni-filter-fill" id={`${props.id}-filter-icon`} aria-hidden />
                    <span className="gs-list-page-filter-btn__label">{BUTTONS.FILTER}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="gs-filter-popover filter-popover-container p-0"
                align="start"
                side="bottom"
                sideOffset={8}
                collisionPadding={16}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <div className="gs-filter-popover__header">
                    <p id={`${props.id}-filter-title`} className="gs-filter-popover__title">
                        {BUTTONS.FILTER}
                    </p>
                </div>
                <div className="gs-filter-popover__body">
                    {props.options && (
                        <FilterSelectField
                            id={`${props.id}-filter-select-status`}
                            placeholder="Status"
                            value={selectedStatus}
                            options={statusOptions}
                            onValueChange={handleStatusChange}
                        />
                    )}
                    {props.title === "Envelopes" && (
                        <Input
                            id={`${props.id}-filter-owner-input`}
                            type="text"
                            className="gs-filter-input mt-2"
                            placeholder="Owner email address"
                            value={ownerSearch}
                            onChange={(e) => {
                                setOwnerSearch(e.target.value);
                                props.setFilter({ ...props.filter, owner: e.target.value });
                            }}
                        />
                    )}
                    {(props.title === "Admins" || props.title === "Users") && (
                        <>
                            <FilterSelectField
                                id={`${props.id}-filter-select-role`}
                                placeholder="Role"
                                value={selectedRole}
                                options={roleOptions}
                                onValueChange={handleRoleChange}
                            />
                            <FilterSelectField
                                id={`${props.id}-filter-select-group`}
                                placeholder="Group"
                                value={selectedGroup}
                                options={groupOptions}
                                onValueChange={handleGroupChange}
                            />
                            <div
                                className="filter-data mt-2 flex items-center"
                                id={`${props.id}-filter-last-login`}
                            >
                                <input
                                    id={`${props.id}-filter-last-login-input`}
                                    placeholder="Last Login At Range"
                                    value={dateRangeLabel(lastLoginAtSelectionRange)}
                                    className="date-input flex-1 border-0 bg-transparent text-sm outline-none"
                                    onClick={() => setLastLoginAtDateOpen(!openLastLoginAtDate)}
                                    readOnly
                                />
                                <em
                                    id={`${props.id}-filter-last-login-input-cross-icon`}
                                    className="icon ni ni-cross font-bold cursor-pointer"
                                    onClick={handleClearLastLoginAtDate}
                                />
                            </div>
                            {openLastLoginAtDate && (
                                <div id={`${props.id}-filter-last-login-date-picker`}>
                                    <DateRange
                                        ranges={[lastLoginAtSelectionRange]}
                                        onChange={handleLastLoginAtSelectRange}
                                        rangeColors={[COLORS.primary]}
                                        maxDate={new Date()}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    <div
                        className="filter-data mt-2 flex items-center"
                        id={`${props.id}-filter-created-at`}
                    >
                        <input
                            id={`${props.id}-filter-created-at-input`}
                            placeholder="Created At Range"
                            value={dateRangeLabel(createdAtSelectionRange)}
                            className="date-input flex-1 border-0 bg-transparent text-sm outline-none"
                            onClick={() => setCreatedAtDateOpen(!openCreatedAtDate)}
                            readOnly
                        />
                        <em
                            id={`${props.id}-filter-created-at-cross-icon`}
                            className="icon ni ni-cross font-bold cursor-pointer"
                            onClick={handleClearCreatedAtDate}
                        />
                    </div>
                    {openCreatedAtDate && (
                        <div id={`${props.id}-filter-created-at-date-picker`}>
                            <DateRange
                                ranges={[createdAtSelectionRange]}
                                onChange={handleCreatedAtSelectRange}
                                rangeColors={[COLORS.primary]}
                                maxDate={new Date()}
                            />
                        </div>
                    )}
                    <div className="filter-footer">
                        <Button size="sm" onClick={handleApplyFilter} id={`${props.id}-apply-filter-btn`}>
                            {BUTTONS.APPLY}
                        </Button>
                        <Button
                            size="sm"
                            variant="light"
                            className="gs-filter-reset-btn"
                            onClick={handleReset}
                            id={`${props.id}-reset-filter-btn`}
                        >
                            {BUTTONS.RESET}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default FilterOverlay;
